import {
  History,
  Close,
  Visibility,
  Edit,
  Download,
  Create,
  Delete,
  Person,
  Computer,
  Schedule,
  Assignment,
} from "@mui/icons-material";
import {
  IconButton,
  Drawer,
  Box,
  Typography,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Avatar,
  Stack,
  Divider,
  Card,
  CardContent,
  Tooltip,
  Badge,
} from "@mui/material";
import CustomTooltip from "components/reusables/CustomTooltip";
import withRouter from "components/withRouter";
import React, { useState } from "react";
import * as url from "./utils/urlHelper";
import { ApiServices } from "services/apiServices";
import { buildUrl } from "services/utils";
import SkeletonTable from "components/SkeletonTable";

const DocumentAuditLogs = (props) => {
  const { recordId } = props,
    { navigate } = props?.router;
  const [openHistory, setOpenHistory] = useState(false);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getData = async () => {
    try {
      setIsLoading(true);
      const queryParams = {
        documentId: recordId,
      };
      const uri = buildUrl(url.GET_DOC_LOG_LIST, queryParams);
      const result = await ApiServices.callServiceGet(uri);
      console.log(result, "result ss");

      if (result?.meta?.success) {
        setData(result?.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const callInitialData = async () => {
    setOpenHistory(true);
    getData();
  };

  const handleNavigate = (row) => {
    if (row?._id) navigate(`/audit-trail/${row._id}`);
  };

  // Get action icon based on action type
  const getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case "open/view":
      case "view":
        return <Visibility fontSize="small" />;
      case "download":
        return <Download fontSize="small" />;
      case "edit":
      case "modify":
      case "update":
        return <Edit fontSize="small" />;
      case "create":
      case "add":
        return <Create fontSize="small" />;
      case "delete":
      case "remove":
        return <Delete fontSize="small" />;
      default:
        return <Assignment fontSize="small" />;
    }
  };

  // Get action color based on action type
  const getActionColor = (action) => {
    switch (action?.toLowerCase()) {
      case "open/view":
      case "view":
        return "info";
      case "download":
        return "secondary";
      case "edit":
      case "modify":
      case "update":
        return "warning";
      case "create":
      case "add":
        return "success";
      case "delete":
      case "remove":
        return "error";
      default:
        return "default";
    }
  };

  // Format date and time
  const formatDateTime = (dateString) => {
    if (!dateString) return { date: "N/A", time: "N/A" };
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name) return "U";
    return name
      ?.split(" ")
      ?.map((n) => n[0])
      ?.join("")
      ?.toUpperCase()
      ?.substring(0, 2);
  };

  const getAuditLogColor = (type) => {
    switch (type) {
      case "User Generated":
        return "success";
      case "System Generated":
        return "info";
      case "Automatic System Generated":
        return "primary";
      default:
        return "default";
    }
  };

  return (
    <>
      <CustomTooltip title="Document Audit Log">
        <IconButton
          onClick={() => callInitialData()}
          sx={{
            background: "grey",
            color: "white",
          }}
        >
          <History />
        </IconButton>
      </CustomTooltip>

      <Drawer
        anchor="right"
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        PaperProps={{
          sx: {
            width: "85%",
            height: "100%",
            maxWidth: "100%",
            top: 0,
            overflowY: "auto",
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          },
        }}
      >
        {/* Header */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 0,
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box display="flex" alignItems="center">
                <History sx={{ mr: 2, fontSize: "2rem" }} />
                <Box>
                  <Typography variant="h5" fontWeight="bold">
                    Document Audit History
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    Track all activities and changes made to this document
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setOpenHistory(false)}
                sx={{
                  color: "white",
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ p: 3 }}>
          {isLoading ? (
            <SkeletonTable />
          ) : data?.length > 0 ? (
            <Card elevation={2} sx={{ borderRadius: 2, overflow: "hidden" }}>
              <TableContainer
                component={Paper}
                sx={{ maxHeight: "calc(100vh - 200px)" }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        Sr. No.
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        Audit ID
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        Date & Time
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        Activity
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        User Details
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        Type
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#f8f9fa",
                          fontWeight: "bold",
                          fontSize: "0.9rem",
                        }}
                      >
                        IP Address
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.map((item, index) => {
                      const dateTime = formatDateTime(item?.createdAt);
                      const userName = item?.userId?.name || "Unknown User";
                      const designation =
                        item?.designationId?.designation || "N/A";

                      return (
                        <TableRow
                          key={index}
                          onClick={() => handleNavigate(item)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "rgba(103, 126, 234, 0.04)",
                              transform: "translateX(4px)",
                              transition: "all 0.2s ease",
                            },
                            "&:nth-of-type(even)": {
                              backgroundColor: "rgba(0, 0, 0, 0.02)",
                            },
                          }}
                        >
                          <TableCell>
                            <Chip
                              label={index + 1}
                              size="small"
                              variant="outlined"
                              sx={{ fontWeight: "bold" }}
                            />
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.8rem",
                                color: "primary.main",
                                fontWeight: "medium",
                              }}
                            >
                              {item?.auditTrailId ?? "N/A"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Stack spacing={0.5}>
                              <Box display="flex" alignItems="center">
                                <Schedule
                                  fontSize="small"
                                  sx={{ mr: 0.5, color: "primary.main" }}
                                />
                                <Typography variant="body2" fontWeight="medium">
                                  {dateTime?.date}
                                </Typography>
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {dateTime?.time}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              icon={getActionIcon(item?.action)}
                              label={item?.action ?? "N/A"}
                              color={getActionColor(item?.action)}
                              variant="outlined"
                              size="small"
                              sx={{ fontWeight: "medium" }}
                            />
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  mr: 1,
                                  bgcolor: "primary.main",
                                  fontSize: "0.8rem",
                                }}
                              >
                                {getUserInitials(userName)}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {userName}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {designation}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={
                                item?.auditLogType
                                  ? item.auditLogType
                                      .replace(/([A-Z])/g, " $1")
                                      .trim()
                                  : "N/A"
                              }
                              size="small"
                              variant="outlined"
                              color={getAuditLogColor(item?.auditLogType)}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </TableCell>

                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Computer
                                fontSize="small"
                                sx={{ mr: 0.5, color: "text.secondary" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.85rem",
                                }}
                              >
                                {item?.ipAddress ?? "N/A"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : (
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                textAlign: "center",
                p: 6,
                background: "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
              }}
            >
              <History
                sx={{ fontSize: "4rem", color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Audit History Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This document has no recorded activities yet.
              </Typography>
            </Card>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default withRouter(DocumentAuditLogs);
